import { AlertTriangle, Briefcase, FileText, Lock, Mail, MapPin, Phone, Scale, Shield, Users } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

const TermsAndConditions = () => {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-8">
            <PageHeader title="Terms and Conditions" subNav="Terms and Conditions" />
            <div className="container mx-auto px-4">

                <div className="my-5 text-sm text-gray-500 font-semibold">
                    Last Updated: May 30, 2025
                </div>

                {/* Content */}
                <div className="">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="sm:p-8 p-5 md:space-y-12 space-y-8">

                            {/* Introduction */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Scale className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Acceptance of Terms</h2>
                                </div>
                                <div className="prose prose-gray max-w-none">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                        Welcome to Argon Medical Systems LLP ("Company," "we," "our," or "us"). These Terms and Conditions ("Terms")
                                        govern your access to and use of our website www.argonmedicalsystems.com (the "Website") and our medical
                                        device products and services (collectively, the "Services").
                                    </p>
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed mt-4">
                                        By accessing or using our Website or Services, you agree to be bound by these Terms and our Privacy Policy.
                                        If you do not agree to these Terms, please do not use our Website or Services. These Terms apply to all
                                        healthcare professionals, institutions, distributors, and other users of our Services.
                                    </p>
                                </div>
                            </section>

                            {/* Eligibility and Registration */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Users className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Eligibility and User Registration</h2>
                                </div>

                                <div className="space-y-6">
                                    <div className="bg-gray-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Professional Use Only</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Our Services are intended for qualified healthcare professionals and institutions</li>
                                            <li className='max-sm:text-sm'>• Users must possess appropriate medical licenses and credentials</li>
                                            <li className='max-sm:text-sm'>• Institutional users must have proper authorization to purchase medical devices</li>
                                            <li className='max-sm:text-sm'>• You must be at least 18 years old to use our Services</li>
                                            <li className='max-sm:text-sm'>• You represent that all information provided is accurate and complete</li>
                                        </ul>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-3">Account Responsibilities</h3>
                                        <ul className="space-y-2 text-blue-800">
                                            <li className='max-sm:text-sm'>• Maintain confidentiality of your account credentials</li>
                                            <li className='max-sm:text-sm'>• Notify us immediately of any unauthorized access</li>
                                            <li className='max-sm:text-sm'>• Accept responsibility for all activities under your account</li>
                                            <li className='max-sm:text-sm'>• Provide accurate and up-to-date information</li>
                                            <li className='max-sm:text-sm'>• Comply with all applicable laws and regulations</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Medical Device Terms */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Briefcase className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Medical Device Products and Services</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="bg-green-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-green-900 mb-3">Product Use and Training</h3>
                                        <ul className="space-y-2 text-green-800">
                                            <li className='max-sm:text-sm'>• Products must be used only by qualified professionals</li>
                                            <li className='max-sm:text-sm'>• Proper training and certification required before use</li>
                                            <li className='max-sm:text-sm'>• Follow all manufacturer instructions and guidelines</li>
                                            <li className='max-sm:text-sm'>• Comply with applicable medical device regulations</li>
                                            <li className='max-sm:text-sm'>• Report adverse events as required by law</li>
                                        </ul>
                                    </div>

                                    <div className="bg-amber-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-amber-900 mb-3">Orders and Delivery</h3>
                                        <ul className="space-y-2 text-amber-800">
                                            <li className='max-sm:text-sm'>• Orders subject to acceptance and availability</li>
                                            <li className='max-sm:text-sm'>• Prices and specifications may change without notice</li>
                                            <li className='max-sm:text-sm'>• Delivery terms as specified in purchase agreements</li>
                                            <li className='max-sm:text-sm'>• Title and risk of loss transfer upon delivery</li>
                                            <li className='max-sm:text-sm'>• Inspection and acceptance procedures apply</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Intellectual Property */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Lock className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Intellectual Property Rights</h2>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6">
                                    <div className="space-y-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Intellectual Property</h3>
                                            <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                                All content, features, and functionality of our Website and Services, including but not limited to text,
                                                graphics, logos, images, software, and medical device designs, are owned by Argon Medical Systems LLP
                                                and are protected by copyright, trademark, patent, and other intellectual property laws.
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Limited License</h3>
                                            <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                                We grant you a limited, non-exclusive, non-transferable license to access and use our Website and
                                                Services for their intended professional medical purposes only. You may not reproduce, modify,
                                                distribute, or create derivative works without our express written permission.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Prohibited Uses */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <AlertTriangle className="w-6 h-6 text-red-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Prohibited Uses and Conduct</h2>
                                </div>

                                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3">You agree NOT to:</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <ul className="space-y-2 text-red-800">
                                            <li className='max-sm:text-sm'>• Use products without proper training or certification</li>
                                            <li className='max-sm:text-sm'>• Modify or reverse engineer our medical devices</li>
                                            <li className='max-sm:text-sm'>• Use our Services for unlawful purposes</li>
                                            <li className='max-sm:text-sm'>• Interfere with the security of our systems</li>
                                            <li className='max-sm:text-sm'>• Transmit malicious code or spam</li>
                                        </ul>
                                        <ul className="space-y-2 text-red-800">
                                            <li className='max-sm:text-sm'>• Violate any applicable laws or regulations</li>
                                            <li className='max-sm:text-sm'>• Infringe on intellectual property rights</li>
                                            <li className='max-sm:text-sm'>• Share account credentials with unauthorized persons</li>
                                            <li className='max-sm:text-sm'>• Use automated systems to access our Website</li>
                                            <li className='max-sm:text-sm'>• Resell products without authorization</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Privacy and Data */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Shield className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Privacy and Data Protection</h2>
                                </div>

                                <div className="bg-blue-50 rounded-lg p-6">
                                    <p className="max-sm:text-sm text-blue-800 leading-relaxed mb-4">
                                        Your privacy is important to us. Our collection, use, and protection of your personal information is
                                        governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our
                                        Services, you consent to the collection and use of your information as described in our Privacy Policy.
                                    </p>
                                    <p className="max-sm:text-sm text-blue-800 leading-relaxed">
                                        We comply with applicable healthcare privacy regulations including HIPAA, GDPR, and other relevant
                                        data protection laws in our handling of personal and health-related information.
                                    </p>
                                </div>
                            </section>

                            {/* Warranties and Disclaimers */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <FileText className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Warranties and Disclaimers</h2>
                                </div>

                                <div className="space-y-6">
                                    <div className="bg-green-50 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-green-900 mb-3">Product Warranties</h3>
                                        <p className="max-sm:text-sm text-green-800 leading-relaxed">
                                            Our medical devices are warranted to be free from defects in materials and workmanship for the
                                            period specified in the applicable product documentation. Specific warranty terms are provided
                                            with each product and may vary by product type and intended use.
                                        </p>
                                    </div>

                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                                        <h3 className="text-lg font-semibold text-amber-900 mb-3">Service Disclaimers</h3>
                                        <p className="max-sm:text-sm text-amber-800 leading-relaxed">
                                            EXCEPT AS EXPRESSLY PROVIDED, OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES
                                            OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES INCLUDING MERCHANTABILITY, FITNESS
                                            FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT TO THE FULLEST EXTENT PERMITTED BY LAW.
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Limitation of Liability */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <AlertTriangle className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Limitation of Liability</h2>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed mb-4">
                                        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ARGON MEDICAL SYSTEMS LLP
                                        BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
                                        OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                                        GOODWILL, OR OTHER INTANGIBLE LOSSES.
                                    </p>
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                        Our total liability for any claims arising out of or relating to these Terms or our Services shall
                                        not exceed the amount paid by you to us in the twelve (12) months preceding the claim, or INR 1,00,000,
                                        whichever is less.
                                    </p>
                                </div>
                            </section>

                            {/* Regulatory Compliance */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Scale className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Regulatory Compliance</h2>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">Medical Device Regulations</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• FDA and CE marking compliance</li>
                                            <li className='max-sm:text-sm'>• ISO 13485 quality management standards</li>
                                            <li className='max-sm:text-sm'>• Indian medical device regulations (MDR)</li>
                                            <li className='max-sm:text-sm'>• Good Manufacturing Practice (GMP) standards</li>
                                            <li className='max-sm:text-sm'>• Post-market surveillance requirements</li>
                                        </ul>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">User Compliance</h3>
                                        <ul className="space-y-2 text-gray-700">
                                            <li className='max-sm:text-sm'>• Follow all applicable medical device laws</li>
                                            <li className='max-sm:text-sm'>• Maintain required licenses and certifications</li>
                                            <li className='max-sm:text-sm'>• Report adverse events as mandated</li>
                                            <li className='max-sm:text-sm'>• Comply with import/export regulations</li>
                                            <li className='max-sm:text-sm'>• Adhere to institutional policies</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>

                            {/* Termination */}
                            <section>
                                <div className="bg-red-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3">Termination</h3>
                                    <p className="max-sm:text-sm text-red-800 leading-relaxed mb-4">
                                        We may terminate or suspend your access to our Services immediately, without prior notice, for any
                                        reason, including but not limited to breach of these Terms, violation of applicable laws, or conduct
                                        that may harm our reputation or other users.
                                    </p>
                                    <p className="max-sm:text-sm text-red-800 leading-relaxed">
                                        Upon termination, your right to use our Services will cease immediately, and we may delete your
                                        account and data. Provisions that by their nature should survive termination shall survive, including
                                        intellectual property rights, limitation of liability, and dispute resolution.
                                    </p>
                                </div>
                            </section>

                            {/* Governing Law */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Scale className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Governing Law and Disputes</h2>
                                </div>

                                <div className="bg-blue-50 rounded-lg p-6">
                                    <p className="max-sm:text-sm text-blue-800 leading-relaxed mb-4">
                                        These Terms shall be governed by and construed in accordance with the laws of India, without regard
                                        to conflict of law principles. Any disputes arising out of or relating to these Terms or our Services
                                        shall be subject to the exclusive jurisdiction of the courts in Surat, Gujarat, India.
                                    </p>
                                    <p className="max-sm:text-sm text-blue-800 leading-relaxed">
                                        We encourage resolution of disputes through good faith negotiation. For certain disputes, we may
                                        require binding arbitration in accordance with the Arbitration and Conciliation Act, 2015.
                                    </p>
                                </div>
                            </section>

                            {/* Changes to Terms */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <FileText className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Changes to These Terms</h2>
                                </div>

                                <p className="max-sm:text-sm text-gray-700 leading-relaxed">
                                    We reserve the right to modify these Terms at any time. We will notify users of material changes by
                                    posting the updated Terms on our Website and updating the "Last Updated" date. Your continued use of
                                    our Services after any changes constitutes acceptance of the new Terms. We recommend reviewing these
                                    Terms periodically for any updates.
                                </p>
                            </section>

                            {/* Contact Information */}
                            <section>
                                <div className="flex items-center gap-3 sm:mb-6 mb-4">
                                    <Mail className="w-6 h-6 text-blue-600" />
                                    <h2 className="md:text-2xl sm:text-xl text-lg font-semibold text-gray-900">Contact Information</h2>
                                </div>

                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
                                    <p className="max-sm:text-sm text-gray-700 leading-relaxed sm:mb-6 mb-4">
                                        If you have questions about these Terms and Conditions or need legal clarification regarding our
                                        Services, please contact us:
                                    </p>

                                    <div className="grid md:grid-cols-2 md:gap-6 gap-4">
                                        <div className="space-y-4">
                                            <div className="flex max-sm:flex-col sm:items-center gap-3">
                                                <Mail className="shrink-0 w-5 h-5 text-blue-600" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Email</p>
                                                    <p className="max-sm:text-sm text-gray-700">argonmedicalsystemsllp@gmail.com</p>
                                                </div>
                                            </div>

                                            <div className="flex max-sm:flex-col sm:items-center gap-3">
                                                <Phone className="shrink-0 w-5 h-5 text-blue-600" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Phone</p>
                                                    <p className="max-sm:text-sm text-gray-700">87581 50698, 79903 29076</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex max-sm:flex-col sm:items-start gap-3">
                                                <MapPin className="shrink-0 w-5 h-5 text-blue-600 mt-1" />
                                                <div>
                                                    <p className="font-semibold text-gray-900">Mailing Address</p>
                                                    <p className="max-sm:text-sm text-gray-700">
                                                        Argon Medical Systems LLP<br />
                                                        412-413, Radhika Optima Near Yamuna chowk, Mota Varachha Surat, Gujarat 394101
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-6 pt-6 border-t border-blue-200">
                                        <p className="text-sm text-gray-600">
                                            For legal matters, please mark your communication as "Legal - Terms and Conditions Inquiry."
                                            We will respond within 30 days of receipt.
                                        </p>
                                    </div>
                                </div>
                            </section>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsAndConditions;